import { useApolloClient } from "@apollo/client"
import React from "react"
import { DELETE_PROMPT } from "../../../mutations/DELETE_PROMPT"
import { PromptsTableContext } from "../contexts/PromptsTableContext"

export const useSelectedPrompts = () => {
    const context = React.useContext(PromptsTableContext)
    if (!context) throw new Error("useSelectedPrompts must be used within a PromptsTableProvider")
    const { setSelectAll, setSelectedPromptUuids, selectedPromptUuids } = context
    const client = useApolloClient()

    const selectAllPrompts = React.useCallback(
        (uuids: string[]) => {
            setSelectedPromptUuids(uuids)
            setSelectAll(true)
        },
        [setSelectedPromptUuids, setSelectAll]
    )

    const unselectAll = React.useCallback(() => {
        setSelectedPromptUuids([])
        setSelectAll(false)
    }, [setSelectedPromptUuids, setSelectAll])

    const isSelected = React.useCallback(
        (uuid: string) => {
            return selectedPromptUuids.includes(uuid)
        },
        [selectedPromptUuids]
    )

    const addPrompt = React.useCallback(
        (uuid: string) => {
            setSelectedPromptUuids([...selectedPromptUuids, uuid])
        },
        [selectedPromptUuids, setSelectedPromptUuids]
    )

    const removePrompt = React.useCallback(
        (uuid: string) => {
            const arr = [...selectedPromptUuids]
            arr.splice(arr.indexOf(uuid), 1)
            setSelectedPromptUuids(arr)
        },
        [selectedPromptUuids, setSelectedPromptUuids]
    )

    const deleteAll = React.useCallback(async () => {
        const mutations = selectedPromptUuids.map(uuid => {
            return {
                uuid: uuid,
                mutation: client.mutate({
                    variables: {
                        mediaUUID: uuid
                    },
                    mutation: DELETE_PROMPT
                })
            }
        })
        let deleteSuccesses = []
        let deleteErrors = []
        await Promise.all(
            mutations.map(({ uuid, mutation }) => {
                return mutation
                    .then(() => {
                        deleteSuccesses.push(uuid)
                    })
                    .catch(err => {
                        deleteErrors.push(uuid)
                    })
            })
        )
        unselectAll()
        return { deleteSuccesses: deleteSuccesses.length, deleteErrors: deleteErrors.length }
    }, [selectedPromptUuids, client, unselectAll])

    return {
        selectAllPrompts: selectAllPrompts,
        unselectAll: unselectAll,
        isSelected: isSelected,
        addPrompt: addPrompt,
        removePrompt: removePrompt,
        deleteAll: deleteAll
    }
}
